import PropTypes from 'prop-types';
import { BsArrowRepeat, BsInfoCircle } from 'react-icons/bs';

import Tooltip from '#globalComponents/Tooltip/Tooltip';
import { formatCount } from '#lib/numberFormat';

import { CREDIT_TYPES, RESERVED_PATTERN, formatRenewal } from '../creditTypes';

export const Help = ({ text }) => (
	<Tooltip content={<span className="block w-56 text-xs">{text}</span>}>
		<BsInfoCircle className="h-3.5 w-3.5 cursor-help text-bgBase-700 hover:text-bgBase-800" />
	</Tooltip>
);

Help.propTypes = { text: PropTypes.string.isRequired };

const percentOf = (value, total) => `${total > 0 ? Math.min(100, (value / total) * 100) : 0}%`;

const PoolBar = ({ used, held, total }) => (
	<div className="flex h-2.5 overflow-hidden rounded-full bg-bgBase-400">
		<div className="h-full bg-primary-600" style={{ width: percentOf(used, total) }} />
		{held > 0 && (
			<div className="h-full" style={{ width: percentOf(held, total), backgroundImage: RESERVED_PATTERN }} />
		)}
	</div>
);

PoolBar.propTypes = {
	used: PropTypes.number.isRequired,
	held: PropTypes.number.isRequired,
	total: PropTypes.number.isRequired,
};

const LegendItem = ({ swatchClass, swatchStyle, children }) => (
	<span className="inline-flex items-center gap-1.5">
		<i className={`h-2.5 w-2.5 rounded-sm ${swatchClass ?? ''}`} style={swatchStyle} />
		{children}
	</span>
);

LegendItem.propTypes = { swatchClass: PropTypes.string, swatchStyle: PropTypes.object, children: PropTypes.node };

const PoolType = ({ type, pool }) => {
	const Icon = type.icon;
	return (
		<div>
			<div className="mb-1.5 flex items-baseline justify-between gap-3">
				<span className="flex items-center gap-1.5 text-sm font-medium text-text">
					<Icon className="h-4 w-4 text-bgBase-700" />
					{type.label}
					<Help text={type.help} />
				</span>
				<span className="whitespace-nowrap text-sm tabular-nums text-text">
					<b>{formatCount(pool.remaining)}</b>{' '}
					<span className="text-bgBase-800">von {formatCount(pool.quota)} übrig</span>
				</span>
			</div>
			<PoolBar used={pool.used} held={pool.held} total={pool.quota} />
			<div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-bgBase-800">
				<LegendItem swatchClass="bg-primary-600">Verbraucht {formatCount(pool.used)}</LegendItem>
				{pool.held > 0 && (
					<LegendItem swatchStyle={{ backgroundImage: RESERVED_PATTERN }}>
						Reserviert für laufende Aufträge {formatCount(pool.held)}
					</LegendItem>
				)}
				<LegendItem swatchClass="bg-bgBase-500">Frei für alle {formatCount(pool.remaining)}</LegendItem>
			</div>
		</div>
	);
};

PoolType.propTypes = { type: PropTypes.object.isRequired, pool: PropTypes.object.isRequired };

/** The org's credit pool this period: what is left per type and when it renews. */
export const CreditPool = ({ pool, renewsAt }) => {
	const renewal = formatRenewal(renewsAt);
	const expiry = renewal ? `am ${renewal}` : 'zum Ende des Abrechnungszeitraums';

	return (
		<div className="grid items-start gap-6 lg:grid-cols-[1fr_auto]">
			<div>
				<div className="mb-3 flex flex-wrap items-center gap-2">
					<span className="text-sm font-semibold text-text">Credits der Organisation diesen Monat</span>
					<Help
						text={`Ihr Plan enthält jeden Monat ${formatCount(pool.company.quota)} Firmen- und ${formatCount(pool.contact.quota)} Kontakt-Credits. Nicht genutzte Credits verfallen ${expiry}.`}
					/>
					{renewal && (
						<span className="ml-auto inline-flex items-center gap-1.5 text-xs text-bgBase-800">
							<BsArrowRepeat className="h-3.5 w-3.5" />
							Erneuert sich am {renewal}
						</span>
					)}
				</div>
				<div className="grid gap-x-8 gap-y-4 md:grid-cols-2">
					{CREDIT_TYPES.map((type) => (
						<PoolType key={type.key} type={type} pool={pool[type.key]} />
					))}
				</div>
			</div>
			<div className="rounded-card border border-bgBase-500 bg-bgBase-300 p-4 lg:w-[260px]">
				<div className="mb-2 text-xs font-semibold text-text">So verteilen sich die Credits</div>
				<p className="text-xs leading-relaxed text-bgBase-800">
					Alle nutzen denselben Topf. Optional ein <b>Monatslimit</b> je Mitglied setzen – mehr ist nicht
					nötig.
				</p>
				<p className="mt-2 text-xs text-success-700">
					Nichts geht verloren, kein Umbuchen zwischen Mitgliedern.
				</p>
			</div>
		</div>
	);
};

const poolShape = PropTypes.shape({
	quota: PropTypes.number.isRequired,
	used: PropTypes.number.isRequired,
	held: PropTypes.number.isRequired,
	remaining: PropTypes.number.isRequired,
});

CreditPool.propTypes = {
	pool: PropTypes.shape({ company: poolShape.isRequired, contact: poolShape.isRequired }).isRequired,
	renewsAt: PropTypes.string,
};
